/**
 * ConfigurationsPage — strategies management page.
 *
 * Phase 3: Aggregations tab removed. Shows only the "Strategies" view.
 * Sidebar shows user strategies; detail panel shows either:
 *   - Strategy detail (params + config list)
 *   - Code viewer (syntax-highlighted code)
 *   - Config detail (runs/trading/versions)
 *
 * Mobile (<md): 3-level stack — show one level at a time.
 * Desktop (>=md): sidebar + detail side-by-side.
 */

import { useConfigurationStore } from '../../stores/configurationStore.js';
import { UserStrategySidebar } from './UserStrategySidebar.js';
import { UserStrategyDetail } from './UserStrategyDetail.js';
import { StrategyCodeViewer } from './StrategyCodeViewer.js';
import { StrategyConfigDetail } from './StrategyConfigDetail.js';

// ============================================================================
// Back button for mobile navigation
// ============================================================================

interface BackButtonProps {
  label: string;
  onClick: () => void;
}

function BackButton({ label, onClick }: BackButtonProps) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-700 shrink-0">
      <button
        onClick={onClick}
        className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 19l-7-7 7-7"
          />
        </svg>
        {label}
      </button>
    </div>
  );
}

// ============================================================================
// Detail panel resolver
// ============================================================================

function StrategiesDetailPanel() {
  const { selectedStrategyId, codeViewOpen, selectedConfigId } = useConfigurationStore();

  if (!selectedStrategyId) {
    return <UserStrategyDetail />;
  }

  if (codeViewOpen) {
    return <StrategyCodeViewer />;
  }

  if (selectedConfigId) {
    return <StrategyConfigDetail />;
  }

  return <UserStrategyDetail />;
}

// ============================================================================
// Main page
// ============================================================================

export function ConfigurationsPage() {
  const {
    selectedStrategyId,
    setSelectedStrategyId,
    codeViewOpen,
    setCodeViewOpen,
    selectedConfigId,
    setSelectedConfigId,
  } = useConfigurationStore();

  // Determine mobile navigation level for strategies tab
  // Level 1: strategy list (no strategy selected)
  // Level 2: strategy detail or code viewer
  // Level 3: config detail
  const mobileLevel = selectedConfigId ? 3 : selectedStrategyId ? 2 : 1;

  const handleMobileBack = () => {
    if (mobileLevel === 3) {
      // Back from config detail → strategy detail
      setSelectedConfigId(null);
    } else if (mobileLevel === 2) {
      if (codeViewOpen) {
        // Back from code → strategy detail
        setCodeViewOpen(false);
      } else {
        // Back from strategy detail → strategy list
        setSelectedStrategyId(null);
      }
    }
  };

  let mobileBackLabel = 'Strategies';
  if (mobileLevel === 3) {
    mobileBackLabel = 'Strategy';
  } else if (mobileLevel === 2 && codeViewOpen) {
    mobileBackLabel = 'Strategy';
  }

  const mobileShowSidebar = mobileLevel === 1;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Content area — no tab bar in Phase 3 */}
      <div className="flex flex-1 overflow-hidden">
        {/* Mobile: show sidebar OR detail (3-level stack) */}
        <div className="flex md:hidden flex-1 flex-col overflow-hidden">
          {mobileShowSidebar ? (
            <div className="flex-1 overflow-y-auto">
              <UserStrategySidebar />
            </div>
          ) : (
            <div className="flex-1 flex flex-col overflow-hidden">
              <BackButton label={mobileBackLabel} onClick={handleMobileBack} />
              <div className="flex-1 overflow-y-auto flex flex-col">
                <StrategiesDetailPanel />
              </div>
            </div>
          )}
        </div>

        {/* Desktop: sidebar + detail side by side */}
        <div className="hidden md:flex flex-1 overflow-hidden">
          <div className="flex-shrink-0 h-full overflow-hidden flex flex-col">
            <UserStrategySidebar />
          </div>
          <div className="flex-1 overflow-hidden flex flex-col">
            <StrategiesDetailPanel />
          </div>
        </div>
      </div>
    </div>
  );
}
